import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import moment from 'moment';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, timeout } from 'rxjs/operators';

import { IonicSlides } from '@ionic/angular';
import { Logger } from 'src/app/common/logging/logger';
import * as models from '../shared/models/model-names';
import { SyncProgressEvent } from '../shared/models/sync-progress-event';
import { EventService } from '../shared/services/event.service';
import { GeneralEntityService } from '../shared/services/general-entity.service';
import { TrackerService } from '../shared/services/tracker.service';
import { XmasService } from '../shared/services/xmas.service';
import { QuoteDto } from './../shared/models/quotes/quote-dto.model';
import { BACKGROUND } from './backgrounds';
import { XMASBACKGROUND } from './xmasBackground';

@Component({
  selector: 'app-sync-indicator',
  templateUrl: './sync-indicator.page.html',
  styleUrls: ['./sync-indicator.page.scss'],
  standalone: false,
})
export class SyncIndicatorPage implements OnInit, OnDestroy {
  @ViewChild('slider') slider: ElementRef | undefined;
  public progressItems: SyncProgressEvent[] = [];
  private _ngUnsubscribe = new Subject();
  public quote = {
    quoteText: 'Tue es, oder tue es nicht. Es gibt kein Versuchen.',
    author: 'Yoda',
  } as QuoteDto;
  public style = {};
  public background: string;
  public showIndicator = false;
  public showCloseDialogIcon = false;
  private backgrounds = BACKGROUND;
  public swiperModules = [IonicSlides];

  constructor(
    private modalController: ModalController,
    public eventService: EventService<SyncProgressEvent>,
    private _platform: Platform,
    private _xmasService: XmasService,
    private _tracker: TrackerService,
    private _logger: Logger,
    private _generalEntityService: GeneralEntityService
  ) {
    if (this._xmasService.isDuringXmasHolidays) {
      this.backgrounds = XMASBACKGROUND;
    }
    const background = !this._platform.is('hybrid')
      ? this.backgrounds[Math.floor(Math.random() * this.backgrounds.length)].desktop
      : this.backgrounds[Math.floor(Math.random() * this.backgrounds.length)].mobile;
    this.style = {
      'background-image': 'url(' + background + ')',
    };
    // Entities that never dispatch a SyncProgressEvent must not get a row: every row starts in the
    // "pending" state (spinner until its first event arrives), so an entity that never reports
    // would spin for the whole run. 'institution' and 'prescription' have no sync service at all
    // (only institutionNote/-Audit/-Contact/-Attribute resp. prescriptionRequest exist),
    // 'portalNotification' has a service but it sets ignoreSync = true and returns before
    // dispatching anything.
    const modelNamesWithoutSyncEvents = [
      'unknown',
      'synctimestamp',
      'logging',
      'icd10',
      'institution',
      'prescription',
      'portalNotification',
    ];
    const modelNames = Object.keys(models);
    modelNames.forEach(modelName => {
      if (!modelNamesWithoutSyncEvents.includes(models[modelName])) {
        this.progressItems.push(new SyncProgressEvent(models[modelName], 0, 0));
      }
    });

    eventService.events.pipe(takeUntil(this._ngUnsubscribe)).subscribe(async progress => {
      if (!(progress instanceof SyncProgressEvent)) {
        return;
      }
      if (progress.isAllTerminal) {
        // Every sync service has reached a terminal state (completed, errored or cancelled) -> close.
        // This replaces the previous "close on the first error" behaviour, which closed the indicator
        // while other services were still syncing.
        await this.dismiss();
        return;
      }
      if (progress.isError) {
        // A single failing service must NOT close the indicator anymore - it is closed once every
        // service is terminal (isAllTerminal above). Failures are reported to Sentry in SyncService.
        // Just flag the affected service so the UI can reflect it. `started` is set too: the
        // service HAS reported (terminally), so its row must leave the pending-spinner state -
        // otherwise a failed entity would spin until the indicator closes.
        const erroredItem = this.progressItems.find(f => f.title === progress.title);
        if (erroredItem) {
          erroredItem.isError = true;
          erroredItem.started = true;
        }
        return;
      }
      const progressItem = this.progressItems.find(f => f.title === progress.title);
      if (!progressItem) {
        progress.started = true;
        this.progressItems.push(progress);
      } else {
        progressItem.progress = progress.progress;
        progressItem.total = progress.total;
        // First event for this entity this run: its count probe has answered, so from here on the
        // numbers are meaningful. Until then the row renders as "pending" (spinner, no numbers).
        progressItem.started = true;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.setupCloseDialogIconVisibility();
    await this.setQuote();
  }

  ionViewDidEnter(): void {
    this.showIndicator = true;
    this._tracker.trackSyncOpen();
  }

  private setupCloseDialogIconVisibility() {
    const eventSubscription = this.eventService.events
      .pipe(
        filter(progress => progress.progress > 0),
        timeout(60_000), // fallback: timeout throws error if there is no sync progress for given amount of time
        take(1),
        takeUntil(this._ngUnsubscribe)
      )
      .subscribe(
        (_ /* next */) => {
          this._logger.info(`[SyncIndicatorPage][canCloseSyncIndicator] showing x, reason: progress`);
          this.showCloseDialogIcon = true;
          eventSubscription.unsubscribe();
        },
        (_ /* error */) => {
          this._logger.info(`[SyncIndicatorPage][canCloseSyncIndicator] showing x, reason: timeout`);
          this._logger.captureErrorWithExtras(
            '[SyncIndicatorPage][canCloseSyncIndicator] showing x, reason: timeout',
            new Error('[SyncIndicatorPage][canCloseSyncIndicator] showing x, reason: timeout'),
            new Map([['component', 'sync-indicator-page']]),
            'error'
          );
          this.showCloseDialogIcon = true;
          eventSubscription.unsubscribe();
        }
      );
  }

  private async setQuote(): Promise<void> {
    const thisDay = moment().format('YYYY-MM-DD');
    this._generalEntityService.type = 'quote';
    const quotes = await this._generalEntityService.query(`date:${thisDay}`);
    if (quotes && quotes.length) {
      const quoteOfTheDay = quotes[0] as QuoteDto;
      this.quote = quoteOfTheDay;
    }
  }
  async goToDataPage() {
    this._tracker.trackSyncDetail();
    await this.slider?.nativeElement.swiper.slideNext(500);
  }
  async goToMottoPage() {
    await this.slider?.nativeElement.swiper.slidePrev(500);
  }

  ngOnDestroy(): void {
    this._ngUnsubscribe.next(null);
    this._ngUnsubscribe.complete();
  }

  public async dismiss() {
    this._tracker.trackSyncClose();
    await this.modalController.dismiss().catch(error => console.log(error));
  }
}
